import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { FormikContext } from 'formik';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete, { AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import MUITextfield from '@mui/material/TextField';
import Typography from '../Typography';
import { FormTrackingContext } from '../Form';
import { useTranslation } from '../../hooks/i18n';
import { CommonProps } from './types';
import { logFieldChange } from '../../utils/tracking';
import { COLORS, ROUNDED_PANEL_SX } from '../../themev2';
import InfoButton from '../InfoButton';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import debounce from '@mui/utils/debounce';
import { ChevronDownIcon } from '../../components/Icons';

type Option = { value: any; label: string; [key: string]: any };

type AutocompleteFieldProps<T extends Option = Option> = CommonProps & {
  value?: string;
  options: T[];
  renderOption?: (option: T, selected: boolean) => ReactNode;
  inputStartAdornment?: ReactNode;
  showShortLabel?: boolean;
  onInputChange?: (input: string) => void;
  debounceInputChange?: number;
  isLoading?: boolean;
  clearable?: boolean;
  roundedPanel?: boolean;
  showChevron?: boolean;
};

export default function AutocompleteField<T extends Option = Option>({
  fullWidth = true,
  error,
  name,
  label,
  hideLabel = false,
  onChange,
  onInputChange,
  debounceInputChange,
  value,
  options,
  placeholder,
  helperText,
  disabled = false,
  eventLogger = logFieldChange,
  optional = false,
  renderOption,
  inputStartAdornment,
  moreInfoTooltip = null,
  showShortLabel = false,
  isLoading = false,
  clearable = false,
  roundedPanel = false,
  showChevron = false,
  sx,
}: AutocompleteFieldProps<T>) {
  const { t } = useTranslation();
  const formik = useContext(FormikContext);
  const _error = error || (formik?.touched[name] && formik?.errors[name]);
  const { trackingEvent } = useContext(FormTrackingContext);
  const _value = formik ? formik.values[name] : value ?? '';
  const [isOpen, setIsOpen] = useState(false);

  const handleClear = () => {
    formik && formik.setFieldValue(name, '');
    onChange && onChange('');
  };

  const handleInputChange = useMemo(
    () =>
      onInputChange && debounceInputChange ? debounce(onInputChange, debounceInputChange) : onInputChange,
    [onInputChange, debounceInputChange]
  );

  return (
    <FormControl fullWidth={fullWidth} error={!!_error}>
      {!hideLabel && (
        <FormLabel
          sx={{
            padding: '8px 0',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Typography>{label}</Typography>
            {!!moreInfoTooltip && <InfoButton title={moreInfoTooltip} />}
          </div>
          {optional && <Typography sx={{ color: COLORS.gray[1000] }}>{t('optional')}</Typography>}
        </FormLabel>
      )}
      <Autocomplete<T, false, boolean, false>
        autoHighlight
        disabled={disabled}
        disableClearable={!clearable}
        onChange={(_, option) => {
          const val = option?.value ?? '';
          formik && formik.setFieldValue(name, val);
          onChange && onChange(val);
          trackingEvent && eventLogger(trackingEvent, name, val);
        }}
        onInputChange={(_, value, reason) => {
          if (reason === 'reset') return;
          handleInputChange && handleInputChange(value);
          trackingEvent && eventLogger(trackingEvent, name, value);
        }}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        open={showChevron ? isOpen : undefined}
        value={
          _value ? options.find((option) => option.value.toString() === _value.toString()) ?? null : null
        }
        renderOption={(props, option, { selected }) => {
          return (
            <MenuItem
              value={option.value}
              {...props}
              sx={{
                '&.Mui-focused, &[aria-selected="true"]': {
                  backgroundColor: `${COLORS.gray[200]} !important`,
                },
              }}
            >
              {renderOption ? renderOption(option, selected) : <div>{option.label}</div>}
            </MenuItem>
          );
        }}
        isOptionEqualToValue={(option, val) => option.value.toString() === val.value?.toString()}
        options={options}
        renderInput={(params) => (
          <MUITextfield
            {...params}
            InputProps={{
              ...params.InputProps,
              sx: {
                // TODO: color: COLORS.gray[1000] and font family for placeholder
                borderRadius: '8px',
                borderColor: COLORS.gray[900],
                '& .MuiInputBase-input': showShortLabel // show shorter label for small autocomplete fields with ellipsis
                  ? {
                      maxWidth: '60%',
                    }
                  : undefined,
              },
              style: {
                padding: '5px',
              },
              startAdornment: inputStartAdornment || null,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="inherit" size={20} style={{ marginRight: 8 }} />}
                  {clearable && _value && (
                    <IconButton
                      onClick={handleClear}
                      size="small"
                      sx={{ color: COLORS.gray[1100], marginRight: '2px' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  {showChevron && (
                    <Box
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setIsOpen((prev) => !prev);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 6px',
                        cursor: 'pointer',
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.15s ease',
                      }}
                    >
                      <ChevronDownIcon width={20} height={20} color={COLORS.gray[1100]} />
                    </Box>
                  )}
                </>
              ),
            }}
            placeholder={placeholder}
          />
        )}
        sx={[
          {
            background: disabled ? COLORS.gray[300] : COLORS.brand.white,
            borderColor: COLORS.gray[900],
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        componentsProps={
          roundedPanel
            ? {
                paper: {
                  sx: ROUNDED_PANEL_SX,
                },
              }
            : undefined
        }
      />
      {(!!_error || helperText) && (
        <FormHelperText sx={{ color: 'inherit', whiteSpace: 'pre-line', fontSize: '14px' }}>
          {_error || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

interface OptionType {
  value: any;
  label: string;
}

interface SearchAutocompleteProps
  extends Omit<AutocompleteProps<OptionType, boolean, boolean, boolean>, 'renderInput' | 'options'> {
  fetchOptions: (searchText: string) => Promise<OptionType[]>;
  debounceInterval?: number;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  optional?: boolean;
  moreInfoTooltip?: string;
  inputStartAdornment?: React.ReactNode;
  renderOption?: (props: any, option: OptionType) => React.ReactNode;
  name: string;
  onChange?: (value: any) => void;
}

export const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  name,
  fetchOptions,
  debounceInterval = 300,
  label,
  placeholder,
  error,
  helperText,
  optional,
  moreInfoTooltip,
  inputStartAdornment,
  renderOption,
  onChange,
  ...autocompleteProps
}) => {
  const formik = useContext(FormikContext);
  const fieldValue = formik?.values[name];
  const fieldError = formik?.touched[name] && formik?.errors[name];
  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(false);

  const debouncedFetchOptions = useCallback(
    debounce(async (searchText: string) => {
      if (searchText === '') return;
      setLoading(true);
      try {
        const fetchedOptions = await fetchOptions(searchText);
        setOptions(fetchedOptions);
      } catch (error) {
        console.error('Error fetching options:', error);
      } finally {
        setLoading(false);
      }
    }, debounceInterval),
    [fetchOptions, debounceInterval]
  );

  const handleInputChange = (event: React.ChangeEvent<{}>, value: string, reason: any) => {
    if (reason === 'reset') return;
    debouncedFetchOptions(value);
  };

  const handleChange = (_: React.SyntheticEvent<Element, Event>, value: any) => {
    formik?.setFieldValue(name, value?.value);
    onChange && onChange(value?.value);
  };

  return (
    <FormControl error={!!fieldError} fullWidth>
      {label && (
        <FormLabel
          sx={{
            padding: '8px 0',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography>{label}</Typography>
            {moreInfoTooltip && <InfoButton title={moreInfoTooltip} />}
          </div>
          {optional && <Typography sx={{ color: COLORS.gray[1000] }}>{'Optional'}</Typography>}
        </FormLabel>
      )}
      <Autocomplete
        {...autocompleteProps}
        options={options}
        onInputChange={handleInputChange}
        loading={loading}
        onChange={handleChange}
        renderOption={
          renderOption ||
          ((props, option) => (
            <MenuItem {...props} key={option.value}>
              {option.label}
            </MenuItem>
          ))
        }
        value={options.find((option) => option.value === fieldValue) || null}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <MUITextfield
            {...params}
            placeholder={placeholder}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              style: {
                padding: '5px',
              },
              startAdornment: inputStartAdornment,
              endAdornment: <>{loading ? <CircularProgress color="inherit" size={20} /> : null}</>,
            }}
            sx={{ borderColor: COLORS.gray[1000] }}
          />
        )}
      />
      {fieldError && (
        <FormHelperText sx={{ color: 'inherit', whiteSpace: 'pre-line', fontSize: '14px' }}>
          {fieldError}
        </FormHelperText>
      )}
    </FormControl>
  );
};
